/**
 * @file GameSceneFactory.ts
 * @role Lazily loads the main game scene and maps validated boot/session state plus grouped
 *       dependencies into its constructor contract.
 * @owns The deferred ReelGameScene module boundary and BootResult-to-scene parameter mapping.
 * @reads Only validated boot output and already-constructed dependencies.
 * @neverDoes Construct core services. Load assets. Call the RGS. Decide game outcomes.
 * @invariants 5 (recovered results remain server-authoritative), 13 (injected dependencies).
 * @see src/app/Bootstrapper.ts, src/app/BootPipeline.ts
 */
import type { BootResult } from './BootPipeline';
import type {
  GamePresentationServices,
  GameSceneConfiguration,
  GameSessionContext,
  ReelGameSceneParams,
} from '@scenes/ReelGameScene/ReelGameScene';
import type { IScene } from '@scenes/IScene';

interface ReelGameSceneConstructor {
  new (params: ReelGameSceneParams): IScene;
}

interface ReelGameSceneModule {
  ReelGameScene: ReelGameSceneConstructor;
}

export interface GameSceneFactoryInput {
  bootResult: BootResult;
  session: Omit<GameSessionContext, 'session' | 'recoveredRound'>;
  presentation: GamePresentationServices;
  configuration: GameSceneConfiguration;
}

type ReelGameSceneLoader = () => Promise<ReelGameSceneModule>;

const loadReelGameScene: ReelGameSceneLoader = () => import('@scenes/ReelGameScene/ReelGameScene');

export class GameSceneFactory {
  private modulePromise: Promise<ReelGameSceneModule> | undefined;

  constructor(private readonly loadModule: ReelGameSceneLoader = loadReelGameScene) {}

  /** Begin fetching the deferred scene chunk. Safe to call repeatedly. */
  preload(): Promise<void> {
    return this.getModule().then(() => undefined);
  }

  async create(input: GameSceneFactoryInput): Promise<IScene> {
    const { ReelGameScene } = await this.getModule();
    const { session, recoveredRound } = input.bootResult;
    return new ReelGameScene({
      session: {
        ...input.session,
        session: {
          sessionId: session.sessionId,
          gameId: session.gameId,
          currency: session.currency,
        },
        ...(recoveredRound ? { recoveredRound } : {}),
      },
      presentation: input.presentation,
      configuration: input.configuration,
    });
  }

  private getModule(): Promise<ReelGameSceneModule> {
    this.modulePromise ??= this.loadModule();
    return this.modulePromise;
  }
}
